/* eslint-disable no-throw-literal */
import gql from 'graphql-tag';
import apolloClient from '../../../../utils/apolloClient';

// ─── GraphQL Query ────────────────────────────────────────────────────────────

const SEARCH_COMPANIES = gql`
  query SearchCompaniesTypeahead($input: SearchInput) {
    search_companies(input: $input) {
      company_listing {
        company_id
        name
        city
        status
        places {
          place_id
          name
          city
          state
          status
          address_line_1
          address_line_2
        }
        kravein_configuration {
          auto_send_invoice
          invoice_cycle
          invoice_email
        }
      }
    }
  }
`;

// ─── Runtime Validators ───────────────────────────────────────────────────────

const COMPANY_STATUSES = new Set(['ACTIVE', 'INACTIVE', 'SUSPENDED']);

const assertCompanyStatus = (val, companyId) => {
  if (!val || !COMPANY_STATUSES.has(val)) {
    console.warn(
      `[companySearchService] Unexpected status: "${val}" for "${companyId}", defaulting to ACTIVE`,
    );
    return 'ACTIVE';
  }
  return val;
};

// ─── Pure Transformers ────────────────────────────────────────────────────────

const toCompany = raw => {
  const companyId = raw?.company_id ?? 'unknown';
  return {
    companyId,
    name: raw?.name ?? '',
    city: raw?.city ?? '',
    status: assertCompanyStatus(raw?.status, companyId),
    autoSendEmail: raw?.kravein_configuration?.auto_send_invoice,
  };
};

const toCompanyList = raw =>
  Array.isArray(raw?.company_listing)
    ? raw.company_listing.filter(item => item !== null).map(toCompany)
    : [];

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === 'object' && err !== null && 'code' in err && 'message' in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('unauthorized') || msg.includes('unauthenticated')) {
      return { message: err.message, code: 'UNAUTHORIZED' };
    }
    if (msg.includes('network') || msg.includes('failed to fetch')) {
      return { message: err.message, code: 'NETWORK_ERROR' };
    }
    return { message: err.message, code: 'UNKNOWN_ERROR' };
  }
  return { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function searchCompanies(
  params,
  client = apolloClient.clientPrivate,
) {
  const { userId, name = '' } = params;

  try {
    const { data } = await client.query({
      query: SEARCH_COMPANIES,
      variables: {
        input: {
          user_id: userId,
          from: 0,
          size: 10,
          filter: {
            company_filter: {
              status: 'ACTIVE',
              ...(name ? { name, place_name: name } : {}),
            },
          },
        },
      },
      fetchPolicy: 'network-only',
    });

    const result = data?.search_companies;

    if (!result) {
      throw {
        message: 'No data returned from search_companies',
        code: 'PARSE_ERROR',
      };
    }

    if (!Array.isArray(result.company_listing)) {
      throw {
        message: 'No data returned from search_companies',
        code: 'PARSE_ERROR',
      };
    }

    return toCompanyList(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

// ─── Export Pure Transformers ─────────────────────────────────────────────────

export { toCompany, toCompanyList };

// ─── Export Service ───────────────────────────────────────────────────────────

export const companySearchService = {
  searchCompanies,
};
